import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faRocket } from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../constants/theme';

const Section = styled.section`
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  padding: 6rem 2rem 2rem;
  position: relative;
  overflow: hidden;
  background: radial-gradient(circle at 50% 50%, rgba(157, 40, 172, 0.1) 0%, rgba(255, 31, 143, 0.1) 50%, rgba(0, 229, 255, 0.1) 100%);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: 1.5rem;
  line-height: 1.2;
  .highlight-pink { color: ${COLORS.pink}; }
  .highlight-purple { color: ${COLORS.purple}; }
  .highlight-cyan { color: ${COLORS.cyan}; }
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  @media (max-width: 968px) { justify-content: center; }
`;

const PrimaryButton = styled(motion.button)`
  padding: 1rem 2rem;
  font-size: 1.1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${COLORS.gradientPrimary};
  color: white;
  transition: transform 0.2s;
  &:hover { transform: translateY(-2px); }
`;

const SecondaryButton = styled(motion.button)`
  padding: 1rem 2rem;
  font-size: 1.1rem;
  border: 2px solid ${COLORS.cyan};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: ${COLORS.cyan};
  transition: transform 0.2s;
  &:hover { transform: translateY(-2px); }
`;

const HeroImage = styled(motion.div)`
  position: relative;
  height: 500px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 90%;
    height: 90%;
    object-fit: contain;
    position: relative;
    z-index: 1;
  }
`;

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

function HeroSection({ onGenerateClick }) {
  const { t } = useTranslation();

  return (
    <Section>
      <Content>
        <div>
          <Title {...fadeUp} transition={{ duration: 0.8 }}>
            <span className="highlight-pink">{t('landing.heroTitle1')}</span><br />
            <span className="highlight-purple">{t('landing.heroTitle2')}</span>
            <span className="highlight-cyan">{t('landing.heroTitle3')}</span>
          </Title>
          <Subtitle {...fadeUp} transition={{ duration: 0.8, delay: 0.2 }}>
            {t('landing.heroSubtitle')}
          </Subtitle>
          <ButtonGroup>
            <PrimaryButton
              id="hero-generate-020"
              onClick={onGenerateClick}
              {...fadeUp}
              transition={{ duration: 0.8, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('landing.generatePersonas')} <FontAwesomeIcon icon={faArrowRight} />
            </PrimaryButton>
            <SecondaryButton
              id="hero-demo-021"
              {...fadeUp}
              transition={{ duration: 0.8, delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('landing.viewDemo')} <FontAwesomeIcon icon={faRocket} />
            </SecondaryButton>
          </ButtonGroup>
        </div>
        <HeroImage {...fadeUp} transition={{ duration: 0.8, delay: 0.4 }}>
          <img src="/hero-illustration.svg" alt="AI-powered persona analysis visualization" />
        </HeroImage>
      </Content>
    </Section>
  );
}

export default HeroSection;
